import { FC, useEffect, useState } from "react";
import {
    amountInDecimal,
    BigText,
    FinishPageType,
    FlowPage,
    FlowPageProps,
    intervalTOKey,
    NormalText,
    SmallestText,
    SmallText,
} from "../cancelFlow";
import {
    StripeSchedule,
    useCurrentStripePlan,
    useStripeInvoice,
    useStripePlans,
    useStripeSchedule,
    useStripeSubscription,
} from "../../../../../components/hooks";
import { useTranslation } from "react-i18next";
import { formatForCurrency } from "../../../../../utils/CurrencySymbol";
import { getLocale } from "../../../../../utils/localeUtils";
import { IonButton, IonLoading, useIonRouter } from "@ionic/react";
import { routes } from "../../../../../route/constants";
import { useSchedulePhases } from "../subscriptionSchedule";
import { SubscriptionService } from "../../../../../services/subscriptionService";
import {
    asPlanType,
    planDisplayName,
} from "../../../../../services/StripeService";
import { ProductBadge } from "../../../../../components/ProductBadge";
import moment from "moment";

export type ConfirmPageProps = FlowPageProps & {
    finishPageType: FinishPageType;
};
const findPlanById = (plans: any[], id: string) => {
    return plans?.find((plan: any) => plan.id === id);
};
export const ConfirmPage: FC<ConfirmPageProps> = ({
    reset,
    finishPageType,
}) => {
    const [schedule, refreshSchedule] = useStripeSchedule();
    const invoice = useStripeInvoice();
    const router = useIonRouter();
    const currentPlan = useCurrentStripePlan();

    const [refreshingSubscription, setRefreshingSubscription] = useState(true);
    useEffect(() => {
        const refresh = async () => {
            await refreshSchedule();
            await SubscriptionService.getInstance()?.refreshSubscription();
            setRefreshingSubscription(false);
        };
        const timeout = setTimeout(refresh, 2000);
        return () => clearTimeout(timeout);
    }, []);
    if (refreshingSubscription) {
        return <IonLoading isOpen={true}></IonLoading>;
    }

    const onFinish = () => {
        if (reset) {
            reset();
        }

        router.push(routes.refsixMoreSubscriptions);
    };
    const page = (() => {
        switch (finishPageType) {
            case FinishPageType.Cancelled:
                return (
                    schedule && (
                        <CancelConfirmationPage
                            finishPageType={finishPageType}
                            schedule={schedule}
                            end={onFinish}
                        />
                    )
                );
            case FinishPageType.OfferSwitch:
                return (
                    schedule && (
                        <ScheduledUpgradeConfirmationPage
                            finishPageType={finishPageType}
                            schedule={schedule}
                            end={onFinish}
                        />
                    )
                );

            case FinishPageType.OfferSwitchInstant:
                return (
                    schedule && (
                        <InstantUpgradeConfirmationPage
                            finishPageType={finishPageType}
                            schedule={schedule}
                            end={onFinish}
                        />
                    )
                );
            case FinishPageType.OfferDiscount:
                return (
                    <OfferDiscountConfirmPage
                        end={onFinish}
                        finishPageType={finishPageType}
                    />
                );
            default:
                return <></>;
        }
    })();
    return page || <></>;
};
type ConfirmSubPageProps = ConfirmPageProps & {
    end: () => void;
};
const OfferDiscountConfirmPage: FC<ConfirmSubPageProps> = ({ reset, end }) => {
    const { t } = useTranslation();
    const sub = useStripeSubscription();

    return (
        <FlowPage className={"offer-discount confirmation-page"}>
            <div className={"content"}>
                <BigText>
                    {t("cancellation.screens.offerDiscountConfirmation.title")}
                </BigText>
                <SmallText>
                    {t(
                        "cancellation.screens.offerDiscountConfirmation.description",
                        {
                            date: moment(sub?.validUntil).format("L"),
                        }
                    )}
                </SmallText>
            </div>
            <IonButton onClick={end}>
                {t("upgrade.proFeatures.close")}
            </IonButton>
        </FlowPage>
    );
};

const CancelConfirmationPage: FC<
    ConfirmSubPageProps & { schedule: StripeSchedule }
> = ({ end }) => {
    const { t } = useTranslation();
    const sub = useStripeSubscription();
    const currentPlan = useCurrentStripePlan();
    const [currentPhase] = useSchedulePhases();

    const endDate = currentPhase?.end_date || sub?.validUntil || 0;
    const planType = asPlanType(sub?.planFeatures || currentPlan?.type);

    return (
        <FlowPage className={"cancel-confirmation confirmation-page"}>
            <ProductBadge type={planType} height={"30px"} />
            <div className={"content"}>
                <div className={"top"}>
                    <NormalText>
                        {t("cancellation.screens.cancelConfirmation.title")}
                    </NormalText>
                    <SmallText>
                        <div>
                            {t(
                                "cancellation.screens.cancelConfirmation.subtitle"
                            )}
                        </div>
                    </SmallText>
                </div>
                <div className={"bottom"}>
                    <SmallestText>
                        {t(
                            "cancellation.screens.cancelConfirmation.description",
                            {
                                plan: planDisplayName(currentPlan?.name),
                                date: new Date(
                                    endDate * 1000
                                ).toLocaleDateString(),
                            }
                        )}
                    </SmallestText>
                </div>
            </div>

            <IonButton onClick={end}>
                {t("upgrade.proFeatures.close")}
            </IonButton>
        </FlowPage>
    );
};

const InstantUpgradeConfirmationPage: FC<
    ConfirmSubPageProps & { schedule: StripeSchedule }
> = ({ reset, schedule, end }) => {
    const { t } = useTranslation();
    const currentPlan = useCurrentStripePlan();
    const plans = useStripePlans();
    const [, nextPhase] = useSchedulePhases();
    const nextPhasePlanId = nextPhase?.plans?.[0]?.plan;
    const nextPlan = findPlanById(plans, nextPhasePlanId);
    const interval = currentPlan?.interval;

    const invoice = useStripeInvoice();
    const currency = nextPlan?.currency || currentPlan?.currency || "USD";
    const locale = getLocale();
    return (
        <FlowPage className={"upgrade-confirmation confirmation-page"}>
            <div className={"content"}>
                <div className={"top"}>
                    <BigText>
                        {t("cancellation.screens.upgradeConfirmation.title")}
                    </BigText>
                </div>
                <div className={"bottom"}>
                    <SmallText>
                        <>
                            <div>
                                {t(
                                    "cancellation.screens.upgradeConfirmation.instantUpgrade",
                                    {
                                        firstInvoice: formatForCurrency(
                                            invoice?.total / 100,
                                            currency,
                                            locale
                                        ),
                                        newPrice: formatForCurrency(
                                            currentPlan?.amount / 100,
                                            currency,
                                            locale
                                        ),
                                        newPlan: planDisplayName(currentPlan),
                                        interval: t(
                                            intervalTOKey(interval)
                                        ).toLowerCase(),
                                    }
                                )}
                            </div>
                        </>
                    </SmallText>
                </div>
            </div>
            <IonButton onClick={end}>
                {t("upgrade.proFeatures.close")}
            </IonButton>
        </FlowPage>
    );
};
const ScheduledUpgradeConfirmationPage: FC<
    ConfirmSubPageProps & { schedule: StripeSchedule }
> = ({ reset, schedule, end }) => {
    const { t } = useTranslation();
    const currentPlan = useCurrentStripePlan();
    const plans = useStripePlans();
    const [currentPhase, nextPhase] = useSchedulePhases();

    const endDate = currentPhase?.end_date || 0;
    const nextPhasePlanId = nextPhase?.plans?.[0]?.plan;
    const nextPlan = findPlanById(plans, nextPhasePlanId);

    const currency = nextPlan?.currency || currentPlan?.currency || "USD";
    const upgradePlanAmount = amountInDecimal(nextPlan?.amount, currency);
    const locale = getLocale();

    const nextPlanPrice = formatForCurrency(
        upgradePlanAmount,
        currency,
        locale
    );
    return (
        <FlowPage className={"upgrade-confirmation confirmation-page"}>
            <div className={"content"}>
                <div className={"top"}>
                    <BigText>
                        {t("cancellation.screens.upgradeConfirmation.title")}
                    </BigText>
                </div>
                <div className={"bottom"}>
                    <SmallText>
                        {t(
                            "cancellation.screens.upgradeConfirmation.description",
                            {
                                currentPlan: planDisplayName(currentPlan),
                                newPlan: planDisplayName(nextPlan),
                                newPrice: nextPlanPrice,
                                date: new Date(
                                    endDate * 1000
                                ).toLocaleDateString(),
                            }
                        )}
                    </SmallText>
                </div>
            </div>
            <IonButton onClick={end}>
                {t("upgrade.proFeatures.close")}
            </IonButton>
        </FlowPage>
    );
};
